import pinIcons from './mapMarkerIcons';

const TABLET_LARGE_MEDIA_QUERY = 768;

/**
 * Gets the width of the page
 *
 * @return {number} - The width of the page window
 */
function getPageWidth() {
  return (
    typeof document !== 'undefined' &&
    Math.max(
      document.body.scrollWidth,
      document.documentElement.scrollWidth,
      document.body.offsetWidth,
      document.documentElement.offsetWidth,
      document.documentElement.clientWidth,
    )
  );
}

/**
 * Calculates how much to offset the safe zone (where all the map markers should be contained) for
 * the map which is located in the center of the map
 *
 * @param {Object} safeZone - The dimensions of the safe zone that the pins will be placed within
 * @param {number} safeZone.width - Width of the safezone
 * @param {number} safeZone.height - Height of the safezone
 * @param {number} safeZone.top - Distance in pixels of the safezone from the top of the page
 * @param {number} safeZone.left - Distance in pixels of the safezone from the left of the page
 * @param {number} contentWidth - The width of both columns of the order status page
 * @param {DOM element} mapEl - The DOM element of the map which it grabs the top position of
 *
 * @return {Offset} - The amount to offset the safe zone from the left and the top of the map
 *
 * @typedef {Object} Offset
 * @param {number} Offset.x - Distance in pixels to offset from the left
 * @param {number} Offset.y - Distance in pixels to offset from the top
 */
export function getMapOffset(safeZone, contentWidth, mapEl) {
  let offset = {
    x: 0,
    y: 0,
  };
  const pageWidth = getPageWidth();

  if (pageWidth && mapEl) {
    // The map is offset from the top of the safeZone
    const mapElRect = mapEl.getBoundingClientRect();
    const mapYOffset = (safeZone.height - mapElRect.height) / 2 + safeZone.top - mapElRect.top;
    let mapXOffset = 0;

    if (pageWidth < TABLET_LARGE_MEDIA_QUERY) {
      // There is only one column for mobile widths
      offset = {
        x: mapXOffset,
        y: mapYOffset,
      };
    } else {
      mapXOffset = -((contentWidth - safeZone.width) / 2);
      offset = {
        x: mapXOffset,
        y: mapYOffset,
      };
    }
  }

  return offset;
}

/**
 * Returns the location with the correct (rider or customer) icon used for Google Maps Marker
 *
 * @param {Location[]} locations - Array of locations
 *
 * @return {LocationWithIcon[]} - Array of locations with an icon property
 *
 * @typedef {Object} Location
 * @param {string} Location.type - Used to determine which icon to assign
 *
 * @typedef {Object} LocationWithIcon
 * @param {object} LocationWithIcon.icon - The icon object suitable for Google Maps Marker
 */
export function getMarkerIcon(locations, riderVehicleType) {
  return locations.map((location) => {
    let pinType = location.type;
    if (location.type === 'RIDER') {
      pinType = riderVehicleType || 'RIDER_DEFAULT';
    }

    return {
      ...location,
      icon: pinIcons[pinType],
    };
  });
}
